/**
 * Base class for custom errors.
 */
class BaseError extends Error {
  /**
   * Creates an instance of BaseError.
   * @param message - The error message.
   */
  constructor(message: string) {
    super(message);
    this.name = 'BaseError';
  }
}

/**
 * Class representing an exception error.
 * Extends the BaseError class.
 */
class ExceptionError extends BaseError {
  /**
   * Creates an instance of ExceptionError.
   * @param message - The error message. Defaults to 'Exception'.
   */
  constructor(message: string = 'Exception') {
    super(message);
    this.name = 'ExceptionError';
  }
}

/**
 * Class representing a server error.
 * Extends the BaseError class.
 */
class ServerError extends BaseError {
  /**
   * Creates an instance of ServerError.
   * @param message - The error message. Defaults to 'Server error'.
   */
  constructor(message: string = 'Server error') {
    super(message);
    this.name = 'ServerError';
  }
}

/**
 * Class representing a requirements not met error.
 * Extends the BaseError class.
 */
class RequirementsNotMetError extends BaseError {
  /**
   * Creates an instance of RequirementsNotMetError.
   * @param message - The error message. Defaults to 'Requirements not met'.
   */
  constructor(message: string = 'Requirements not met') {
    super(message);
    this.name = 'RequirementsNotMetError';
  }
}

/**
 * Class representing an unsupported error.
 * Extends the BaseError class.
 */
class UnsupportedError extends BaseError {
  /**
   * Creates an instance of UnsupportedError.
   * @param message - The error message. Defaults to 'Unsupported'.
   */
  constructor(message: string = 'Unsupported') {
    super(message);
    this.name = 'UnsupportedError';
  }
}

/**
 * Class representing a not implemented error.
 * Extends the BaseError class.
 */
class NotImplementedError extends BaseError {
  /**
   * Creates an instance of NotImplementedError.
   * @param message - The error message. Defaults to 'Not implemented'.
   */
  constructor(message: string = 'Not implemented') {
    super(message);
    this.name = 'NotImplementedError';
  }
}

/**
 * Class representing a not found error.
 * Extends the BaseError class.
 */
class NotFoundError extends BaseError {
  /**
   * Creates an instance of NotFoundError.
   * @param message - The error message. Defaults to 'Not found'.
   */
  constructor(message: string = 'Not found') {
    super(message);
    this.name = 'NotFoundError';
  }
}

/**
 * Class representing a not authorized error.
 * Extends the BaseError class.
 */
class NotAuthorizedError extends BaseError {
  /**
   * Creates an instance of NotAuthorizedError.
   * @param message - The error message. Defaults to 'Not authorized.'.
   */
  constructor(message: string = 'Not authorized.') {
    super(message);
    this.name = 'NotAuthorizedError';
  }
}

/**
 * Class representing a request cancelled error.
 * Extends the BaseError class.
 */
class RequestCancelledError extends BaseError {
  /**
   * Creates an instance of RequestCancelledError.
   * @param message - The error message. Defaults to 'Request cancelled'.
   */
  constructor(message: string = 'Request cancelled') {
    super(message);
    this.name = 'RequestCancelledError';
  }
}

/**
 * Class representing a connection error.
 * Extends the BaseError class.
 */
class ConnectionError extends BaseError {
  /**
   * Creates an instance of ConnectionError.
   * @param message - The error message. Defaults to 'Connection failed'.
   */
  constructor(message: string = 'Connection failed') {
    super(message);
    this.name = 'ConnectionError';
  }
}

/**
 * Class representing a timeout error.
 * Extends the BaseError class.
 */
class TimeoutError extends BaseError {
  /**
   * Creates an instance of TimeoutError.
   * @param message - The error message. Defaults to 'Timed out'.
   * @param retryAfterSeconds - Optional retry after seconds.
   */
  constructor(
    message: string = 'Timed out',
    public retryAfterSeconds?: number,
  ) {
    super(message);
    this.name = 'TimeoutError';
  }
}

/**
 * Class representing a duplicate entry error.
 * Extends the BaseError class.
 */
class DuplicateEntryError extends BaseError {
  /**
   * Creates an instance of DuplicateEntryError.
   * @param message - The error message. Defaults to 'Duplicate entry'.
   */
  constructor(message: string = 'Duplicate entry') {
    super(message);
    this.name = 'DuplicateEntryError';
  }
}

export {
  BaseError,
  ExceptionError,
  ServerError,
  RequirementsNotMetError,
  NotAuthorizedError,
  NotFoundError,
  NotImplementedError,
  ConnectionError,
  TimeoutError,
  DuplicateEntryError,
  UnsupportedError,
  RequestCancelledError,
};
